import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { deepCamelCaseKeys } from 'src/utils/deepCamelCaseKeys';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';

export interface TrainerClient {
  uid: string;
  email: string | null;
  bodyProfile?: string;
  name: string;
  surname: string;
  trainerIds: string[];
  avatarUrl: string | null;
  createdAt: string;
  isFitnessComplete?: boolean;
  is_fitness_complete?: boolean;
  isActive?: boolean;
  is_active?: boolean;
  isBlocked?: boolean;
  is_blocked?: boolean;
  blockedAt?: string;
  blockReason?: string;
}

export interface FetchTrainerClientsParams {
  trainerId: string;
  status: string;
  search: string;
  sortBy: string;
  page: number;
  limit?: number;
  clientId?: string;
}

export interface TrainerClientsResponse {
  clients: TrainerClient[];
  totalCount: number;
  page: number;
  limit: number;
}

export const fetchTrainerClients = async ({
  trainerId,
  status,
  search,
  sortBy,
  page,
  limit,
  clientId,
}: FetchTrainerClientsParams): Promise<TrainerClientsResponse> => {
  if (!trainerId) {
    throw new Error('Trainer ID is missing');
  }

  const queryParams = new URLSearchParams({
    trainerId,
    status,
    search,
    sort: sortBy,
    page: page.toString(),
  });

  if (limit) {
    queryParams.append('limit', limit.toString());
  }

  if (clientId) {
    queryParams.append('clientId', clientId);
  }

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_CLIENTS}?${queryParams.toString()}`;

  const response = await fetch(apiUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch trainer clients: ${response.status}`);
  }

  const res = await response.json();
  const parsedData = deepCamelCaseKeys(res.data);

  return {
    clients: parsedData as unknown as TrainerClient[],
    totalCount: res.totalCount || 0,
    page: res.page || 1,
    limit: res.limit || 20,
  };
};

export function useGetTrainerClients(
  status: string = 'active',
  search: string = '',
  sortBy: string = 'newest',
  page: number = 1,
  limit: number = 10,
) {
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid || auth.currentUser?.uid;

  return useQuery({
    queryKey: ['trainer-clients', trainerId, status, search, sortBy, page, limit],
    queryFn: () =>
      fetchTrainerClients({ trainerId: trainerId as string, status, search, sortBy, page, limit }),
    enabled: !!trainerId,
  });
}

/**
 * Fetches a single client by their UID using the backend's clientId filter.
 * Only enabled when clientId is provided.
 */
export function useGetTrainerClientById(clientId?: string) {
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid || auth.currentUser?.uid;

  return useQuery({
    queryKey: ['trainer-client-by-id', trainerId, clientId],
    queryFn: () =>
      fetchTrainerClients({
        trainerId: trainerId as string,
        status: 'all',
        search: '',
        sortBy: 'newest',
        page: 1,
        limit: 1,
        clientId,
      }),
    enabled: !!trainerId && !!clientId,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });
}
